import React from 'react';

import type { WebpagePreviewData } from '~/components/AiChat/types';
import { Text } from '~/components/core';
import { LinkOutIcon } from '~/components/icons';
import colors from '~/theme/tailwind/colors';

/**
 * WebLinkPreview
 *
 * Business logic component for web URL previews.
 * Displays Open Graph metadata (title, description, image) when available,
 * with fallback to basic URL information.
 */

interface WebLinkPreviewData {
  hostname: string;
  url: string;
}

interface WebLinkPreviewProps {
  url: string;
  preview?: WebpagePreviewData;
  isLoading?: boolean;
}

const parseWebUrl = (url: string): WebLinkPreviewData | null => {
  try {
    const urlObject = new URL(url);
    const hostname = urlObject.hostname.replace('www.', '');

    return {
      hostname,
      url,
    };
  } catch {
    return null;
  }
};

export const WebLinkPreview: React.FC<WebLinkPreviewProps> = ({ url, preview, isLoading = false }) => {
  const parsedUrl = parseWebUrl(url);

  if (!parsedUrl) {
    return (
      <div className="w-[400px] p-12">
        <Text variant={Text.VARIANTS.SM} className="text-slate-600">
          Invalid URL
        </Text>
      </div>
    );
  }

  // Show skeleton loading state while fetching
  if (isLoading) {
    return (
      <div className="w-[400px] border-slate-200 bg-white">
        <div className="p-12">
          {/* Skeleton for image */}
          <div className="mb-8 h-[200px] w-full animate-pulse rounded bg-slate-200" />

          {/* Skeleton for hostname */}
          <div className="mb-4 flex items-center gap-4">
            <div className="h-[12px] w-[12px] animate-pulse rounded bg-slate-200" />
            <div className="h-[10px] w-[120px] animate-pulse rounded bg-slate-200" />
          </div>

          {/* Skeleton for title */}
          <div className="mb-4 space-y-2">
            <div className="h-[14px] w-full animate-pulse rounded bg-slate-200" />
            <div className="h-[14px] w-3/4 animate-pulse rounded bg-slate-200" />
          </div>

          {/* Skeleton for description */}
          <div className="space-y-2">
            <div className="h-[12px] w-full animate-pulse rounded bg-slate-200" />
            <div className="h-[12px] w-2/3 animate-pulse rounded bg-slate-200" />
          </div>
        </div>
      </div>
    );
  }

  // Compute display values outside render
  const hasOgData = preview && !preview.error && (preview.title || preview.description || preview.image_url);
  const displayImage = hasOgData && preview.image_url ? preview.image_url : null;
  const displayTitle = hasOgData && preview.title ? preview.title : parsedUrl.hostname;
  const displayDescription = hasOgData && preview.description ? preview.description : parsedUrl.url;

  return (
    <div className="w-[400px] rounded-lg border-slate-200 bg-white">
      <div className="p-12">
        {/* Show OG image if available */}
        {displayImage && (
          <div className="mb-8">
            <img
              src={displayImage}
              alt={displayTitle}
              className="h-auto w-full rounded object-cover"
              style={{ maxHeight: '200px' }}
              onError={(e) => {
                // Hide image if it fails to load
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Hostname with icon */}
        <div className="mb-4 flex items-center gap-4 text-slate-500">
          <LinkOutIcon className="h-[12px] w-[12px]" iconColor={colors.slate[700]} />
          <Text variant={Text.VARIANTS.XXS} className="text-slate-700">
            {parsedUrl.hostname}
          </Text>
        </div>

        {/* Title */}
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mb-4 line-clamp-2">
          {displayTitle}
        </Text>

        {/* Description */}
        <Text variant={Text.VARIANTS.XS} className="line-clamp-2">
          {displayDescription}
        </Text>
      </div>
    </div>
  );
};
